import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Input } from '@codes/ui-kit/components/ui/input';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { Camera } from 'lucide-react';

import { useUpdateProfile, useUpdateUserPhoto } from '@codes/auth';
import { useLoader } from '@codes/loader';
import { Avatar, AvatarFallback, AvatarImage, SharedSpinner } from '@codes/ui-kit';
import { UserView, useWebCoreStore } from '@codes/web-core';

interface NameEditFormProps {
    currentName: string;
    ownerId: string;
    onSuccess?: (user: UserView) => void;
}

export const NameEditForm = ({ currentName, ownerId, onSuccess }: NameEditFormProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState(currentName);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { setIsLoading } = useLoader();
    const inputRef = useRef<HTMLInputElement>(null);
    const updateUserPhotoFromStore = useWebCoreStore(state => state.updateUserPhoto);

    const profile = useWebCoreStore(state => state.profile);
    const { mutate: updateProfile } = useUpdateProfile(
        (user: UserView) => {
            setIsSubmitting(false);
            onSuccess?.(user);
        },
        error => {
            setIsSubmitting(false);
            toast({ title: error?.message });
        }
    );

    const { mutateAsync: updateUserPhoto } = useUpdateUserPhoto(
        () => {},
        error => {
            setIsSubmitting(false);
            toast({ title: error?.message });
        }
    );

    const handleNameUpdate = () => {
        setIsSubmitting(true);
        updateProfile({ name: name.trim(), id: profile.uid });
    };

    const imageBtnTapped = () => {
        inputRef.current?.click();
    };

    const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target?.files?.[0];
        e.target.value = '';
        if (!file) {
            return;
        }
        setIsLoading(true);
        try {
            const user = await updateUserPhoto({ file, userId: profile.uid });
            updateUserPhotoFromStore(user);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (currentName) {
            setName(currentName);
        }
    }, [currentName]);

    return (
        <div className="flex items-start gap-5">
            {/* Avatar */}
            <div className="relative shrink-0">
                <button
                    onClick={imageBtnTapped}
                    className="w-7 h-7 rounded-full bg-primary absolute -bottom-0.5 -right-0.5 z-10 flex items-center justify-center hover:bg-primary-hover transition-colors shadow-sm border-2 border-card"
                >
                    <Camera size={13} className="text-white" />
                </button>
                <input
                    type="file"
                    onChange={handleFileInputChange}
                    className="hidden"
                    ref={inputRef}
                    accept="image/*"
                />
                <Avatar className="w-16 h-16 bg-default dark:bg-hover p-0 ring-2 ring-border">
                    <AvatarImage src={profile?.$user?.photo} alt={currentName} />
                    <AvatarFallback className="text-lg font-semibold">
                        {currentName?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                </Avatar>
            </div>

            {/* Name + Edit */}
            <div className="flex-1 min-w-0 pt-1">
                <label className="text-xs text-text-500 mb-1.5 block">{t('myPage.name')}</label>
                <div className="flex gap-2">
                    <Input
                        placeholder={t('myPage.enterName')}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        disabled={isSubmitting}
                        className="flex-1"
                    />
                    <Button
                        onClick={handleNameUpdate}
                        disabled={isSubmitting || !name.trim() || name.trim() === currentName}
                        className="shrink-0"
                    >
                        {isSubmitting ? <SharedSpinner /> : t('myPage.edit')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
